import { useLazyQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { config } from 'appConfig';
import {
  logPosthogEvent,
  stopTracking,
  userIdentifyEvent,
  userRegisterEvent,
} from 'components/providers/AppPosthogProvider';
import { useFeatureFlags } from 'featureFlags/useFeatureFlags';
import { useCallback } from 'react';
import { TeamNode, UserNode } from 'typeDeclarations/graphql/nodes';
import { captureEvent as captureGTMEvent } from './gtm';

const USER_BLACKLIST = new Set([
  'gbn@leadzai.com',
  'gbn+staff@leadzai.com',
  'gbn+pa-pt@leadzai.com',
  'gbn+sitee@leadzai.com',
  'gbn+fcr-be@leadzai.com',
  'gbn+bizay@leadzai.com',
  'probely_scanner@advertio.com',
]);

export interface UserTrackingQueryFragmentData {
  sessionTeam: Pick<TeamNode, 'id' | '_id' | 'name' | 'isDemo' | 'modified'> & { owner: Pick<UserNode, 'id' | '_id'> };
  sessionUser: Pick<UserNode, 'id' | '_id' | 'name' | 'email' | 'theme' | 'created' | 'isStaff'>;
}

export const USER_TRACKING_QUERY_FRAGMENT = gql(/* GraphQL */ `
  fragment UserTrackingQueryFragment on Query {
    sessionTeam {
      id
      _id
      name
      isDemo
      modified
      owner {
        id
        _id
      }
    }
    sessionUser {
      id
      _id
      name
      email
      theme
      isStaff
      modified
    }
  }
`);

type UserTrackingQueryData = UserTrackingQueryFragmentData;

const USER_TRACKING_QUERY = gql(/* GraphQL */ `
  query userTrackingQuery {
    ...UserTrackingQueryFragment
  }
`);

function shouldTrackEmail(email: string) {
  return !USER_BLACKLIST.has(email);
}

interface UseUserTrackingReturnValue {
  identifyUser: () => Promise<void>;
  captureEvent: (args: { eventName: string; anonymous?: boolean; data?: Record<string, unknown> }) => Promise<void>;
}

export const useUserTracking = (): UseUserTrackingReturnValue => {
  const [fetchUserTrackingData] = useLazyQuery<UserTrackingQueryData>(USER_TRACKING_QUERY);
  const { isFeatureEnabled } = useFeatureFlags();

  // Should only be invoked when the user is known to be logged-in
  const identifyUser = useCallback<UseUserTrackingReturnValue['identifyUser']>(async () => {
    if (!config.posthogShouldTrack) {
      stopTracking();
      return;
    }

    const { data } = await fetchUserTrackingData();
    if (!data) return;

    const {
      sessionTeam: { isDemo, name: teamName, _id: teamPrettyId },
      sessionUser: { email, theme, isStaff, name: userName, _id: userPrettyID },
    } = data;

    if (!shouldTrackEmail(email)) {
      stopTracking();
      return;
    }

    const userIdentity = {
      userId: userPrettyID,
      email,
      theme,
      isStaff,
      name: userName,
    };

    const teamIdentity = {
      teamName,
      isDemoTeam: isDemo,
      teamId: teamPrettyId,
    };

    userIdentifyEvent(userPrettyID, { ...userIdentity });
    userRegisterEvent({ ...userIdentity, ...teamIdentity });

    captureGTMEvent('identifyUser', { ...userIdentity, ...teamIdentity });
  }, [fetchUserTrackingData]);

  const captureEvent = useCallback<UseUserTrackingReturnValue['captureEvent']>(
    async ({ data, eventName, anonymous = false }) => {
      if (!config.posthogShouldTrack) {
        stopTracking();
        return;
      }

      /**
       * The user is considered anonymous if they're not logged-in. This is useful on auth-related pages.
       * Note: GQL operations executed on logged-out users always redirect to an auth page
       */
      // FIXME: We should request the query isLoggedIn to know this
      if (!anonymous) {
        // TODO: Can't we just fetch this data once, upon login?
        //  And, in the future, update it when the user changes their settings?
        const { data: queryData } = await fetchUserTrackingData();
        if (!queryData) return;

        const { sessionUser } = queryData;
        if (!shouldTrackEmail(sessionUser.email)) {
          stopTracking();
          return;
        }
      }

      // Add frontend settings to the event data
      const eventDataWithFrontendSettings = {
        ...data,
        frontendTheme: config.theme,
        frontendEnvironment: config.appEnvironment,
        frontendIsLocalhost: config.isLocalhost,
        andorraOptOut: isFeatureEnabled('andorra-opt-out'),
        isStaffUi: isFeatureEnabled('is-staff-ui'),
      };

      logPosthogEvent(eventName, {
        extra: eventDataWithFrontendSettings,
      });

      captureGTMEvent(eventName, data);
    },
    [fetchUserTrackingData, isFeatureEnabled],
  );

  return {
    identifyUser,
    captureEvent,
  };
};
